<template>
  <footer class="footer">
    <div class="footer-container">
      <div class="footer-content">
        <div class="footer-brand">
          <span class="footer-logo">Partycord</span>
          <p class="footer-tagline">Making voice channels fun and easy</p>
        </div>
        
        <div class="footer-links">
          <div class="footer-section">
            <h3 class="footer-title">Quick Links</h3>
            <ul class="footer-list">
              <li><router-link to="/" class="footer-link">Home</router-link></li>
              <li><router-link to="/commands" class="footer-link">Commands</router-link></li>
              <li><router-link to="/guides" class="footer-link">Guides</router-link></li>
              <li><router-link to="/changelog" class="footer-link">Changelog</router-link></li>
              <li><a href="https://discord.com/invite/beaStFd" class="footer-link" target="_blank" rel="noopener noreferrer">Support Server</a></li>
            </ul>
          </div>
          
          <div class="footer-section">
            <h3 class="footer-title">Support</h3>
            <ul class="footer-list">
              <li><a href="https://www.patreon.com/partycord" class="footer-link" target="_blank" rel="noopener noreferrer">Become a Patron</a></li>
              <li><a href="https://discord.com/oauth2/authorize?client_id=756856376201969716&permissions=947326938200&scope=bot%20applications.commands" class="footer-link" target="_blank" rel="noopener noreferrer">Invite Bot</a></li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="footer-bottom">
        <p class="footer-copyright">© 2020-{{ currentYear }} Kyle Gorr - Partycord. All rights reserved.</p>
        <div class="footer-social">
          <a href="https://discord.com/invite/beaStFd" class="social-link" target="_blank" rel="noopener noreferrer" aria-label="Join our Discord server">
            <fa :icon="['fab', 'discord']" />
          </a>
          <a href="https://www.patreon.com/partycord" class="social-link" target="_blank" rel="noopener noreferrer" aria-label="Support us on Patreon">
            <fa :icon="['fab', 'patreon']" />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'FooterMenu',
  computed: {
    currentYear() {
      return new Date().getFullYear()
    }
  }
}
</script>

<style scoped>
.footer {
  background: linear-gradient(135deg, var(--color-bg-secondary) 0%, var(--color-bg-primary) 100%);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  position: relative;
}

.footer-container {
  max-width: var(--container-max-width);
  margin: 0 auto;
  padding: var(--spacing-xxl) var(--container-padding) var(--spacing-lg) var(--container-padding);
}

.footer-content {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: var(--spacing-xxl);
  align-items: flex-start;
  margin-bottom: var(--spacing-xl);
}

.footer-brand {
  max-width: 300px;
}

.footer-logo {
  font-size: var(--font-size-xl);
  font-weight: bold;
  background: var(--color-gradient-primary);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: block;
  margin-bottom: var(--spacing-xs);
}

.footer-tagline {
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  margin: 0;
  line-height: 1.4;
}

.footer-links {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-xl);
}

.footer-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.footer-title {
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
  font-weight: 600;
  margin: 0;
}

.footer-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.footer-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  font-size: var(--font-size-sm);
  transition: color var(--transition-normal);
  position: relative;
}

.footer-link:hover {
  color: var(--color-text-primary);
}

.footer-link:focus-visible {
  outline: 2px solid var(--color-accent-discord);
  outline-offset: 2px;
  border-radius: 2px;
}

.footer-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: var(--spacing-lg);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.footer-copyright {
  color: var(--color-text-muted);
  font-size: var(--font-size-xs);
  margin: 0;
}

.footer-social {
  display: flex;
  gap: var(--spacing-md);
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  color: var(--color-text-secondary);
  font-size: 1.2rem;
  text-decoration: none;
  transition: all var(--transition-normal);
}

.social-link:hover {
  background: var(--color-accent-discord);
  color: var(--color-text-primary);
  transform: translateY(-2px);
}

.social-link:focus-visible {
  outline: 2px solid var(--color-text-primary);
  outline-offset: 2px;
}

.social-link[href*="patreon"]:hover {
  background: var(--color-accent-patreon);
}

/* Responsive Design */
@media (max-width: 768px) {
  .footer-content {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
    text-align: center;
  }
  
  .footer-brand {
    max-width: none;
  }
  
  .footer-links {
    grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: var(--spacing-lg);
  }
  
  .footer-bottom {
    flex-direction: column;
    gap: var(--spacing-md);
    text-align: center;
  }
}

@media (max-width: 480px) {
  .footer-container {
    padding: var(--spacing-xl) var(--container-padding) var(--spacing-md) var(--container-padding);
  }
  
  .footer-links {
    grid-template-columns: 1fr;
    gap: var(--spacing-md);
  }
  
  .footer-section {
    text-align: left;
  }
  
  .footer-logo {
    font-size: var(--font-size-lg);
  }
  
  .footer-social {
    justify-content: center;
  }
}
</style>
